import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Avatar, Tooltip, IconButton, RadioGroup, FormControlLabel, Radio, TextField, Button } from '@mui/material';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { ZoomIn, ZoomOut, RestartAlt, Rotate90DegreesCw, OpenInNew, Download, Print, KeyboardArrowRight } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getKzmDetails } from '../services/materialApprovalsService';
import api from '../services/api';
import { API_BASE_URL } from '../services/api';

const MaterialApprovalApprovePage: React.FC = () => {
  const { kzmId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Decyzja: Akceptacja bez uwag, Akceptacja z uwagami, Brak akceptacji
  const [decision, setDecision] = React.useState<'accept'|'accept_with_notes'|'reject'>('accept');
  const [comment, setComment] = React.useState('');
  const [decisionDate, setDecisionDate] = React.useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });

  // Podgląd PDF (UI-only) – na razie bez pobierania z backendu
  const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
  const [scale, setScale] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);

  const isCommentDisabled = decision === 'accept';
  const previewUrl = React.useMemo(() => {
    if (!pdfUrl) return null;
    try {
      // Spróbuj wyciągnąć fileId z różnych formatów linków GDrive/GDocs
      const m1 = String(pdfUrl).match(/\/file\/d\/([a-zA-Z0-9_-]+)/); // drive file preview
      const m2 = String(pdfUrl).match(/id=([a-zA-Z0-9_-]+)/); // uc?export=download&id=
      const m3 = String(pdfUrl).match(/\/d\/([a-zA-Z0-9_-]+)/); // document/d/{id}/...
      const fileId = (m1 && m1[1]) || (m2 && m2[1]) || (m3 && m3[1]) || null;
      if (fileId) return `${API_BASE_URL}/kzms/attachments/${encodeURIComponent(fileId)}/content`;
    } catch {}
    return pdfUrl; // fallback – może być publiczny
  }, [pdfUrl]);

  React.useEffect(() => {
    (async () => {
      try {
        const u = new URL(window.location.href);
        const qpdf = u.searchParams.get('pdf');
        if (qpdf) { setPdfUrl(qpdf); return; }
      } catch {}
      // Fallback: pobierz szczegóły KZM i użyj generated_kzm_pdf_url lub podglądu GDocs
      try {
        if (!kzmId) return;
        const det: any = await getKzmDetails(kzmId);
        const pdf = det?.kzm?.generated_kzm_pdf_url || null;
        if (pdf) { setPdfUrl(pdf); return; }
        const gdocId = det?.kzm?.generated_kzm_google_doc_id;
        if (gdocId) {
          setPdfUrl(`https://docs.google.com/document/d/${gdocId}/preview`);
        }
      } catch {
        setPdfUrl(null);
      }
    })();
  }, [kzmId]);

  return (
    <Box sx={{ p: 2 }}>
      {/* Pasek nagłówka */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Tooltip title={user?.display_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || 'Użytkownik'}>
          <Avatar src={user?.avatar_url || undefined} sx={{ width: 28, height: 28 }}>
            {(user?.first_name || user?.last_name) ? `${(user.first_name||'').slice(0,1)}${(user.last_name||'').slice(0,1)}`.toUpperCase() : 'U'}
          </Avatar>
        </Tooltip>
        <KeyboardArrowRight sx={{ color: 'text.secondary' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{`Zatwierdzenie KZM ${kzmId}`}</Typography>
      </Box>

      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2 }}>
        {/* Lewa kolumna – viewer PDF */}
        <Box sx={{ width: { xs: '100%', md: '70%' } }}>
          <Box sx={{ border: '2px dashed #9e9e9e', borderRadius: 1, height: { xs: '70vh', md: 'calc(100vh - 180px)' }, display:'flex', alignItems:'center', justifyContent:'center', position:'relative', overflow:'hidden', bgcolor: pdfUrl ? 'background.paper' : 'transparent' }}>
            {/* Toolbar wyłączony na prośbę – zachowujemy czysty viewer */}
            {previewUrl ? (
              <Box sx={{ width:'100%', height:'100%', transform:`scale(${scale}) rotate(${rotation}deg)`, transformOrigin:'center center' }}>
                <iframe title="kzm-pdf" src={previewUrl} style={{ width:'100%', height:'100%', border:0 }} />
              </Box>
            ) : (
              <Box sx={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'text.secondary' }}>
                Brak podglądu PDF karty KZM
              </Box>
            )}
          </Box>
        </Box>

        {/* Prawa kolumna – decyzja */}
        <Paper sx={{ p: 2, width: { xs: '100%', md: '30%' } }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Decyzja</Typography>
          <RadioGroup value={decision} onChange={(e)=>setDecision(e.target.value as any)}>
            <FormControlLabel value="accept" control={<Radio />} label="Akceptacja bez uwag" />
            <FormControlLabel value="accept_with_notes" control={<Radio />} label="Akceptacja z uwagami" />
            <FormControlLabel value="reject" control={<Radio />} label="Brak akceptacji" />
          </RadioGroup>
          <TextField
            label="Data"
            type="date"
            value={decisionDate}
            onChange={(e)=> setDecisionDate(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField label="Komentarz" value={comment} onChange={(e)=>setComment(e.target.value)} multiline minRows={4} fullWidth sx={{ mt: 2 }} disabled={isCommentDisabled} placeholder={isCommentDisabled ? 'Komentarz nie wymagany dla Akceptacji' : 'Wpisz komentarz…'} />
          <Box sx={{ display:'flex', justifyContent:'flex-end', gap: 1, mt: 2 }}>
            <Button variant="outlined" onClick={()=>navigate(-1)}>Anuluj</Button>
            <Button variant="contained" onClick={async()=>{
              try {
                if (!kzmId) return;
                const payload = {
                  decision: decision === 'accept' ? 'Akceptacja bez uwag' : decision === 'accept_with_notes' ? 'Akceptacja z uwagami' : 'Brak akceptacji',
                  remarks: comment || null,
                  date: decisionDate || null,
                } as any;
                await api.post(`/kzms/${encodeURIComponent(kzmId)}/approve-decision`, payload);
                // Powiadom inne zakładki o zmianie decyzji (storage event)
                try {
                  const key = 'kzm_decision_updated';
                  const val = JSON.stringify({ kzm_id: kzmId, decision: payload.decision, date: payload.date, ts: Date.now() });
                  localStorage.setItem(key, val);
                  // szybko wyczyść, żeby kolejne zmiany też odpalały event
                  setTimeout(() => { try { localStorage.removeItem(key); } catch {} }, 50);
                } catch {}
                // Po sukcesie wróć do listy i pokaż komunikat
                try { (window as any).alert && alert('Zapisano decyzję'); } catch {}
                // Wymuś odświeżenie listy KZM
                window.location.href = '/app/materials_approvals';
              } catch {}
            }}>Zapisz</Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MaterialApprovalApprovePage;


